"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ModeToggle } from "@/components/mode-toggle"
import { Search, Menu, X } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu"

export default function Navbar() {
  const pathname = usePathname()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/drinks?search=${encodeURIComponent(searchQuery)}`
    }
  }

  // Menu structure with dropdowns
  const menu = [
    {
      name: "Home",
      href: "/",
    },
    {
      name: "Cocktails",
      sub: [
        { name: "Easy Cocktails", href: "/cocktails/easy" },
        { name: "Classic Cocktails", href: "/cocktails/classic" },
        { name: "Viral Cocktails", href: "/cocktails/viral" },
        { name: "Fruity and Flirty", href: "/cocktails/fruity-flirty" },
        { name: "Low Cal Cocktails", href: "/cocktails/low-cal" },
        { name: "Shorts and Shooters", href: "/cocktails/shorts-shooters" },
      ],
    },
    {
      name: "Mocktails",
      sub: [
        { name: "Fruity and Fresh", href: "/mocktails/fruity-fresh" },
        { name: "Cooler and Slushies", href: "/mocktails/cooler-slushies" },
        { name: "Mood Boosters", href: "/mocktails/mood-boosters" },
        { name: "For The Gram", href: "/mocktails/for-the-gram" },
        { name: "Sober But Sexy", href: "/mocktails/sober-sexy" },
      ],
    },
    {
      name: "Mood",
      sub: [
        { name: "Feeling Flirty", href: "/mood/flirty" },
        { name: "Lit and Loud", href: "/mood/lit-loud" },
        { name: "Classy AF", href: "/mood/classy-af" },
        { name: "Heartbreak Mix", href: "/mood/heartbreak" },
        { name: "Boss Mode", href: "/mood/boss" },
      ],
    },
    {
      name: "Occasions",
      sub: [
        { name: "Girls Night", href: "/occasions/girls-night" },
        { name: "Date Night", href: "/occasions/date-night" },
        { name: "Birthday Bash", href: "/occasions/birthday-bash" },
        { name: "Brunch and Bubbles", href: "/occasions/brunch-bubbles" },
        { name: "After Work", href: "/occasions/after-work" },
        { name: "Family Get-together", href: "/occasions/family-get-together" },
      ],
    },
    {
      name: "Ask Bartender",
      href: "/ask-bartender",
    },
    {
      name: "Ingredients",
      sub: [
        { name: "Alcoholic Bases", href: "/ingredients/alcoholic-bases" },
        { name: "Mixers and Syrups", href: "/ingredients/mixers-syrups" },
        { name: "Fruits and Juices", href: "/ingredients/fruits-juices" },
        { name: "Herbs and Extra", href: "/ingredients/herbs-extra" },
      ],
    },
    {
      name: "Blogs",
      sub: [
        { name: "Mixology Tips", href: "/blogs/mixology-tips" },
        { name: "Party Vibes", href: "/blogs/party-vibes" },
        { name: "Hangover Hacks", href: "/blogs/hangover-hacks" },
        { name: "Behind The Bar", href: "/blogs/behind-the-bar" },
        { name: "Ingredient Deep Dive", href: "/blogs/ingredient-deep-dive" },
        { name: "Mocktail Culture", href: "/blogs/mocktail-culture" },
      ],
    },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm" : "bg-background"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link href="/" className="flex items-center">
              <span className="text-2xl font-bold text-primary">69</span>
              <span className="text-2xl font-bold ml-1">Cocktails</span>
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-1">
            {menu.map((item) =>
              item.sub ? (
                <div key={item.name} className="relative group">
                  {/* Hover logic for desktop dropdown */}
                  <button
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${pathname.startsWith(`/${item.name.toLowerCase()}`) ? "text-primary" : "text-foreground/70 hover:text-foreground hover:bg-accent"}`}
                    tabIndex={0}
                    aria-haspopup="true"
                    aria-expanded="false"
                    type="button"
                  >
                    {item.name}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <div className="min-w-[180px] absolute left-0 mt-2 z-[9999] bg-popover/95 backdrop-blur-md border rounded-xl shadow-xl opacity-0 group-hover:opacity-100 group-hover:translate-y-1 transition-all duration-200 pointer-events-none group-hover:pointer-events-auto scale-95 group-hover:scale-100 flex flex-col py-2">
                    {item.sub.map((sub) => (
                      <Link
                        key={sub.name}
                        href={sub.href}
                        className={`block px-4 py-2 text-sm transition-all rounded-lg ${pathname === sub.href ? "text-primary bg-accent" : "text-foreground/80 hover:text-foreground hover:bg-accent/80"}`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${pathname === item.href ? "text-primary" : "text-foreground/70 hover:text-foreground hover:bg-accent"}`}
                >
                  {item.name}
                </Link>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="search"
                placeholder="Search drinks..."
                className="w-64 pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </form>
            <ModeToggle />
          </div>

          <div className="flex md:hidden items-center space-x-4">
            <ModeToggle />
            <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-t animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="search"
                placeholder="Search drinks..."
                className="w-full pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </form>

            <nav className="flex flex-col space-y-2">
              {menu.map((item) =>
                item.sub ? (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 w-full text-left"
                        tabIndex={0}
                        aria-haspopup="true"
                        aria-expanded="false"
                        type="button"
                      >
                        {item.name}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-[180px] mt-2">
                      {item.sub.map((sub) => (
                        <DropdownMenuItem asChild key={sub.name}>
                          <Link
                            href={sub.href}
                            className={`block px-4 py-2 text-sm transition-colors rounded-md ${pathname === sub.href ? "text-primary bg-accent" : "text-foreground/80 hover:text-foreground hover:bg-accent"}`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      pathname === item.href
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/70 hover:text-foreground hover:bg-accent"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}
